import React from 'react';
import { Package, TrendingUp, AlertTriangle, AlertOctagon, CheckCircle2 } from 'lucide-react';
import { calculateNetStock, calculateDOI, getTrafficLight, formatNumber } from '../utils/calculations';
import { getEnrichedProduct } from '../utils/productEnrichment';

const InventoryCard = ({ item, forecastFactor = 0, isSelected, onClick }) => {
    const netStock = calculateNetStock(item);
    const doi = calculateDOI(netStock, item.AvgDailySales, forecastFactor);
    const status = getTrafficLight(netStock, doi);
    const enriched = getEnrichedProduct(item.SKU);

    // Dynamic Styling Config based on Status
    const config = {
        red: {
            border: 'border-l-red-500',
            bg: 'bg-red-50/50',
            text: 'text-red-700',
            icon: AlertOctagon,
            bar: 'bg-red-500',
            glow: 'shadow-[inset_4px_0_0_0_#ef4444]'
        },
        yellow: {
            border: 'border-l-amber-500',
            bg: 'bg-amber-50/50',
            text: 'text-amber-700',
            icon: AlertTriangle,
            bar: 'bg-amber-500',
            glow: 'shadow-[inset_4px_0_0_0_#f59e0b]'
        },
        green: {
            border: 'border-l-emerald-500',
            bg: 'bg-white',
            text: 'text-emerald-700',
            icon: CheckCircle2,
            bar: 'bg-emerald-500',
            glow: 'shadow-[inset_4px_0_0_0_#10b981]'
        }
    };

    const style = config[status] || config.green;
    const StatusIcon = style.icon;

    // Stock Level Bar Calculation (Visual max = 100 or actual stock if higher)
    const maxBarValue = Math.max(100, netStock);
    const barPercentage = Math.max(0, Math.min(100, (netStock / maxBarValue) * 100));

    return (
        <div
            onClick={onClick}
            dir="rtl"
            className={`
                relative bg-white rounded-xl shadow-sm border border-slate-200 p-4 
                transition-all duration-200 active:scale-[0.98]
                ${style.glow}
                ${isSelected ? 'ring-2 ring-slate-900 ring-offset-2' : ''}
            `}
        >
            {/* Top Row: Info & Image */}
            <div className="flex gap-4">
                {/* Product Image / Placeholder */}
                <div className="flex-shrink-0 w-16 h-16 bg-slate-100 rounded-lg overflow-hidden border border-slate-100 flex items-center justify-center">
                    {enriched?.imageUrl ? (
                        <img src={enriched.imageUrl} alt="" className="w-full h-full object-cover mix-blend-multiply" />
                    ) : (
                        <Package className="text-slate-300" size={24} />
                    )}
                </div>

                {/* Text Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                        <h3 className="text-sm font-bold text-slate-900 leading-tight line-clamp-2 ml-2">
                            {item.Description}
                        </h3>
                        {/* Status Icon */}
                        <StatusIcon size={18} className={`${style.text} flex-shrink-0`} />
                    </div>
                    <p className="text-xs font-mono text-slate-500 mt-1 trunacte">{item.SKU}</p>
                </div>
            </div>

            {/* Middle Row: Metrics Grid */}
            <div className="grid grid-cols-2 gap-4 mt-4 mb-2">
                {/* Net Stock */}
                <div className="bg-slate-50 rounded-lg p-2 border border-slate-100 flex flex-col items-center">
                    <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">מלאי נטו</span>
                    <span className={`text-xl font-mono font-black tracking-tight ${style.text}`}>
                        {formatNumber(netStock)}
                    </span>
                </div>

                {/* DOI */}
                <div className="bg-slate-50 rounded-lg p-2 border border-slate-100 flex flex-col items-center">
                    <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">ימי מלאי (DOI)</span>
                    <div className="flex items-baseline gap-1">
                        <span className="text-xl font-mono font-black tracking-tight text-slate-700">
                            {doi > 999 ? '999+' : doi}
                        </span>
                        <span className="text-[10px] text-slate-400">ימים</span>
                    </div>
                </div>
            </div>

            {/* Bottom Row: Visual Bar & Secondary Stats */}
            <div className="mt-3 space-y-2">
                {/* Visual Stock Bar */}
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ${style.bar}`}
                        style={{ width: `${barPercentage}%` }}
                    />
                </div>

                <div className="flex justify-between items-center text-[10px] font-medium text-slate-400 px-1">
                    <span>פיזי: <span className="font-mono text-slate-600">{formatNumber(item.PhysicalStock)}</span></span>
                    <span>מכר: <span className="font-mono text-slate-600">{formatNumber(item.Velocity)}</span>/חודש</span>
                </div>
            </div>
        </div>
    );
};

export default InventoryCard;
